import React from 'react'
import { Column } from '@vp/swan'
import Tile from '../TileRows/Tile'
import { getUrlIdForTracking } from '../../utils/getUrlIdForTracking'
import { getTileCtaTrackingConfig, getNewTileTrackingConfig } from '../../Analytics/trackingUtils'
import { useTrackingContext } from '../../hooks/trackingContext'
import { TileWrapper, type TileProps } from '@vp/tile-wrapper'
import { createOnClickTrackingHandler } from '../../Analytics/trackingUtils'
import { useLogger } from '@vp/ubik-context'
import { TILE_ROW_SCHEMA } from '../../utils/trackingSchemas'
import { trackMapEvent } from '../../Analytics/mapTracking'
import { type MapTrackingConfig } from '../../shared/types'

interface NewTileData {
  contentful_id: string
  template: TileProps
}

interface LegacyTileData {
  transformedUrl: unknown
  productImage: unknown
  mpv?: unknown
  merchandisingElement?: unknown
  productTileHeader?: unknown
}

type TileData = NewTileData | LegacyTileData

interface TilesListProps {
  list: TileData[]
  loadType: string
  promoCodeText: string
  trackingPageZone: string
  useNewTiles: boolean
  locale: string
  usesMap: boolean
  mapMetadata?: MapTrackingConfig
}

const TilesList = (props: TilesListProps) => {
  const { list, loadType, promoCodeText, trackingPageZone, useNewTiles, locale, usesMap, mapMetadata } = props
  const { sourcePagePath } = useTrackingContext()
  const logger = useLogger()
  const columnSpan = list && list.length % 6 === 0 ? 2 : 3

  const getTrackingConfiguration = (tile: LegacyTileData, position: number) => {
    const urlId = getUrlIdForTracking(tile.transformedUrl)
    const ctaValue = tile.mpv?.mpvId || tile.merchandisingElement?.mpvId || urlId || tile.productTileHeader
    return getTileCtaTrackingConfig(sourcePagePath, tile.transformedUrl?.url, trackingPageZone, ctaValue, position)
  }

  const getTrackingDataNewTile = (tile: NewTileData, position: number) => {
    const tileHeader = tile.template?.tileDetails?.header?.value ?? ''
    const destinationUrl = tile.template?.tileDetails?.link?.href ?? ''

    const trackingData = {
      tileHeader,
      destinationUrl,
      position,
      productKey: tile.template?.productKey ?? '',
      mpvId: tile.template?.productsList?.selectedProduct?.mpvId,
      sourcePagePath: typeof window !== 'undefined' ? window.location.pathname : '/offers',
      pageZone: trackingPageZone,
    }
    return trackingData
  }

  const trackingMethod = (tile: NewTileData, position: number) => (event: React.MouseEvent) => {
    const trackingData = getTrackingDataNewTile(tile, position)
    if (!trackingData) return

    const trackingAndNavigation = createOnClickTrackingHandler({
      trackingConfig: getNewTileTrackingConfig(trackingData),
      logger,
      schema: TILE_ROW_SCHEMA,
      href: tile.template?.tileDetails?.link?.href,
      openLinkNewTab: undefined,
      callback: undefined,
      mapTrackingConfig: mapMetadata,
      mapSchema: TILE_ROW_SCHEMA,
    })
    if (trackingAndNavigation) {
      trackingAndNavigation(event)
    }
  }

  React.useEffect(() => {
    if (usesMap && mapMetadata) {
      const { pageZone, placementVariantId, placementDefinitionId } = mapMetadata
      trackMapEvent(pageZone, placementVariantId, placementDefinitionId, 'view', logger, TILE_ROW_SCHEMA)
    }
  }, [])

  return (
    <>
      {list &&
        list.map((tile: TileData, index) => (
          <Column component="li" span={columnSpan} paddingLeft={{ xs: 2, md: 3 }} spanXs={6} key={index} marginBottom="between-subsections">
            {useNewTiles && !usesMap ? (
              <TileWrapper
                tileProps={(tile as NewTileData).template}
                locale={locale}
                clickHandler={trackingMethod(tile, index + 1)}
                extraProps={{
                  shouldHideAutomaticTags: true,
                  enableCampaignCallouts: true,
                  primaryLinkConfig: { skin: 'unstyled' },
                }}
                hideDescription={!(tile as NewTileData).template.tileDetails?.description?.key /* this indicates a user provided description */}
              />
            ) : (
              <Tile
                loadType={loadType}
                data={tile as LegacyTileData}
                promoCodeText={promoCodeText}
                trackingConfiguration={getTrackingConfiguration(tile as LegacyTileData, index + 1)}
                usesMap={usesMap}
                mapMetadata={mapMetadata}
              />
            )}
          </Column>
        ))}
    </>
  )
}

export default TilesList
